import { useState } from 'react';
import { Alert, Box, Grid, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import PageContainer from '../../../components/layout/PageContainer';
import {
  FBoxContainer,
  FHButton,
  FHInputField,
  FHPageLink,
  FProgressBar,
} from '../../../components/base';
import { fetchWrapper } from '../../../helpers';
import { API_ENDPOINT } from '../../../constants';
import { useChangeProviderEmail } from './hooks';
import { CHANGE_PROVIDER_EMAIL_PASSWORD } from './constants';

function ChangeProviderEmail() {
  const { getAccessTokenSilently } = useAuth0();
  const { loading: changeLoading, error, success, changeEmail, reset } = useChangeProviderEmail();

  const [password, setPassword] = useState('');
  const [providerIdInput, setProviderIdInput] = useState('');
  const [currentEmail, setCurrentEmail] = useState<string | null>(null);
  const [providerName, setProviderName] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const isPasswordVerified = password === CHANGE_PROVIDER_EMAIL_PASSWORD;
  const loading = lookupLoading || changeLoading;

  const handleLookup = async () => {
    const id = Number(providerIdInput);
    if (!id) return;

    setLookupLoading(true);
    setLookupError(null);
    setCurrentEmail(null);
    setProviderName(null);
    setNewEmail('');
    setShowConfirm(false);
    reset();

    try {
      const provider = await fetchWrapper.get({
        url: `${API_ENDPOINT}/providers/${id}/`,
        getAccessTokenSilently,
      });
      setCurrentEmail(provider.email);
      setProviderName(`${provider.first_name} ${provider.last_name}`);
    } catch (err: unknown) {
      setLookupError(err instanceof Error ? err.message : 'Provider not found');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleConfirm = async () => {
    const id = Number(providerIdInput);
    if (!id || !newEmail) return;

    await changeEmail(id, newEmail);
    setShowConfirm(false);
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  const handleReset = () => {
    setProviderIdInput('');
    setCurrentEmail(null);
    setProviderName(null);
    setLookupError(null);
    setNewEmail('');
    setShowConfirm(false);
    reset();
  };

  return (
    <PageContainer>
      {loading && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer
          dataTestId="changeProviderEmailForm"
          sx={{ borderRadius: 2, width: '100%', boxShadow: 'none' }}
        >
          <Grid container>
            <Grid item xs={12} md={12} lg={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <Typography textAlign={'left'} variant="h4">
                    Change Provider Email
                  </Typography>
                </Box>
                <Box
                  sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                />
                <Grid container p={0.5} spacing={1} alignItems="center">
                  <Grid item xs={2.5}>
                    <Typography textAlign={'left'}>Password:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FHInputField
                      inputProps={{
                        'data-testid': 'changeProviderEmailPasswordInput',
                      }}
                      type="password"
                      autoComplete="off"
                      inputHeight={40}
                      sx={{ boxShadow: 'none' }}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={!!password && !isPasswordVerified}
                      infoText={
                        password.length === 0
                          ? ''
                          : isPasswordVerified
                            ? 'Password verified'
                            : 'Password does not match'
                      }
                    />
                  </Grid>
                </Grid>

                {isPasswordVerified && (
                  <>
                    <Grid container p={0.5} spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <Grid item xs={2.5}>
                        <Typography textAlign={'left'}>Provider ID:</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <FHInputField
                          inputProps={{
                            'data-testid': 'providerIdInput',
                          }}
                          type="number"
                          inputHeight={40}
                          sx={{ boxShadow: 'none' }}
                          value={providerIdInput}
                          onChange={(e) => setProviderIdInput(e.target.value)}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FHButton
                          data-testid="lookupProviderButton"
                          variant="contained"
                          onClick={handleLookup}
                          disabled={!providerIdInput || loading}
                        >
                          Look up
                        </FHButton>
                      </Grid>
                    </Grid>

                    {lookupError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {lookupError}
                      </Alert>
                    )}

                    {currentEmail && (
                      <>
                        <Grid container p={0.5} spacing={1} alignItems="center" sx={{ mt: 1 }}>
                          <Grid item xs={2.5}>
                            <Typography textAlign={'left'}>Provider:</Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography textAlign={'left'}>{providerName}</Typography>
                          </Grid>
                        </Grid>

                        <Grid container p={0.5} spacing={1} alignItems="center">
                          <Grid item xs={2.5}>
                            <Typography textAlign={'left'}>Current email:</Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <Typography textAlign={'left'}>{currentEmail}</Typography>
                          </Grid>
                        </Grid>

                        {!success && (
                          <>
                            <Alert severity="info" sx={{ mt: 2 }}>
                              Before changing a provider&apos;s email, make sure you have confirmed
                              that the provider owns the new email address. This will update their
                              login and they will need to sign in again with the new email.
                            </Alert>

                            <Grid container p={0.5} spacing={1} alignItems="center" sx={{ mt: 1 }}>
                              <Grid item xs={2.5}>
                                <Typography textAlign={'left'}>New email:</Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <FHInputField
                                  inputProps={{
                                    'data-testid': 'newEmailInput',
                                  }}
                                  type="email"
                                  inputHeight={40}
                                  sx={{ boxShadow: 'none' }}
                                  value={newEmail}
                                  onChange={(e) => {
                                    setNewEmail(e.target.value);
                                    setShowConfirm(false);
                                  }}
                                  disabled={loading}
                                />
                              </Grid>
                            </Grid>

                            {!showConfirm && (
                              <FHButton
                                data-testid="changeEmailButton"
                                variant="contained"
                                sx={{ mt: 2, width: '200px' }}
                                onClick={() => setShowConfirm(true)}
                                disabled={!newEmail || newEmail === currentEmail || loading}
                              >
                                Change Email
                              </FHButton>
                            )}

                            {showConfirm && (
                              <Alert severity="warning" sx={{ mt: 2 }}>
                                <Typography>
                                  Change email from <strong>{currentEmail}</strong> to{' '}
                                  <strong>{newEmail}</strong>? The provider will need to sign in
                                  again.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                  <FHButton
                                    data-testid="confirmChangeEmailButton"
                                    variant="contained"
                                    onClick={handleConfirm}
                                    disabled={loading}
                                  >
                                    Confirm
                                  </FHButton>
                                  <FHButton
                                    data-testid="cancelChangeEmailButton"
                                    variant="outlined"
                                    onClick={handleCancel}
                                    disabled={loading}
                                  >
                                    Cancel
                                  </FHButton>
                                </Box>
                              </Alert>
                            )}

                            {error && (
                              <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                              </Alert>
                            )}
                          </>
                        )}

                        {success && (
                          <Alert severity="success" sx={{ mt: 2 }}>
                            <Typography>
                              Email changed successfully. The provider must sign in again with the
                              new email.
                            </Typography>
                            <FHButton
                              data-testid="resetButton"
                              variant="outlined"
                              sx={{ mt: 2 }}
                              onClick={handleReset}
                            >
                              Change another
                            </FHButton>
                          </Alert>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default ChangeProviderEmail;
